import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import { useMetaTags } from '../../services/useMetaTags';
import NotFoundPage from '../Errors/NotFound';
import {parseRouteResponse} from '../../services/route-response';
import '../../assets/styles/certification/create.scss';

const DRAFT_STATUS = 1;
const PORTFOLIO_REQUEST_TYPE = 2;

const EMPTY_FORM = {
    type: '',
    qualificationCategory: '',
    speciality: '',
    username: '',
    birthdayDate: '',
    universityName: '',
    yearEnd: '',
    faculty: '',
    workplaceName: '',
    workplacePositionName: '',
    score: '',
    experience: '',
    certificationRequest: [],
    diploma: [],
    employmentHistory: [],
    specialistCertificate: [],
    qualificationCategoryCertificate: [],
    partTimeWork: [],
    portfolio: [],
    portfolioConfirmation: [],
};

function normalizeFileList(list) {
    if (!Array.isArray(list)) {
        return [];
    }

    return list
        .filter((file) => file && (file.id || file.fileIdentifier))
        .map((file) => ({
            id: String(file.id || file.fileIdentifier),
            name: String(file.name || file.id || file.fileIdentifier),
        }));
}

function extractFileIds(list) {
    if (!Array.isArray(list)) {
        return [];
    }

    return list
        .filter((file) => file && file.id && !file.uploading)
        .map((file) => String(file.id));
}

function buildPatchFormData(form) {
    const payload = new FormData();

    const scalarFields = [
        'type',
        'qualificationCategory',
        'speciality',
        'username',
        'birthdayDate',
        'universityName',
        'yearEnd',
        'faculty',
        'workplaceName',
        'workplacePositionName',
        'score',
        'experience',
    ];

    scalarFields.forEach((fieldName) => {
        payload.append(fieldName, String(form[fieldName] || ''));
    });

    const listFields = [
        'certificationRequest',
        'diploma',
        'employmentHistory',
        'specialistCertificate',
        'qualificationCategoryCertificate',
        'partTimeWork',
        'portfolio',
        'portfolioConfirmation',
    ];

    listFields.forEach((fieldName) => {
        extractFileIds(form[fieldName]).forEach((fileId) => {
            payload.append(`${fieldName}[]`, fileId);
        });
    });

    return payload;
}

function isFilled(value) {
    return String(value || '').trim() !== '';
}

function isPreviewReady(form) {
    const requiredFields = [
        'birthdayDate',
        'experience',
        'faculty',
        'score',
        'universityName',
        'username',
        'workplaceName',
        'workplacePositionName',
        'yearEnd',
    ];

    return requiredFields.every((fieldName) => isFilled(form[fieldName]));
}

function isCertificationReady(form) {
    const requiredFields = [
        'birthdayDate',
        'experience',
        'faculty',
        'qualificationCategory',
        'score',
        'speciality',
        'type',
        'universityName',
        'username',
        'workplaceName',
        'workplacePositionName',
        'yearEnd',
    ];

    const requiredFiles = [
        'certificationRequest',
        'diploma',
        'employmentHistory',
        'specialistCertificate',
        'portfolio',
        'portfolioConfirmation',
    ];

    return requiredFields.every((fieldName) => isFilled(form[fieldName]))
        && requiredFiles.every((fieldName) => extractFileIds(form[fieldName]).length > 0);
}

function isPortfolioReady(form) {
    return extractFileIds(form.portfolio).length > 0
        && extractFileIds(form.portfolioConfirmation).length > 0;
}

function normalizeInitialForm(payload) {
    const next = {...EMPTY_FORM};
    const isPortfolioRequest = Number(payload?.requestType) === PORTFOLIO_REQUEST_TYPE;

    if (!isPortfolioRequest) {
        next.type = String(payload?.certificationData?.type || '');
        next.qualificationCategory = String(payload?.certificationData?.qualificationCategory || '');
        next.speciality = String(payload?.certificationData?.speciality || '');
        next.username = String(payload?.certificationPaper?.username || '');
        next.birthdayDate = String(payload?.certificationPaper?.birthdayDate || '');
        next.universityName = String(payload?.certificationPaper?.universityName || '');
        next.yearEnd = String(payload?.certificationPaper?.universityYearEnd || '');
        next.faculty = String(payload?.certificationPaper?.faculty || '');
        next.workplaceName = String(payload?.certificationPaper?.workplaceName || '');
        next.workplacePositionName = String(payload?.certificationPaper?.workplacePositionName || '');
        next.score = String(payload?.certificationPaper?.score || '');
        next.experience = String(payload?.certificationPaper?.workplaceExperience || '');
        next.certificationRequest = normalizeFileList(payload?.certificationRequest?.files || []);
        next.diploma = normalizeFileList(payload?.certificationDocuments?.diploma || []);
        next.employmentHistory = normalizeFileList(payload?.certificationDocuments?.employmentHistory || []);
        next.specialistCertificate = normalizeFileList(payload?.certificationDocuments?.specialistCertificate || []);
        next.qualificationCategoryCertificate = normalizeFileList(payload?.certificationDocuments?.qualificationCategoryCertificate || []);
        next.partTimeWork = normalizeFileList(payload?.certificationDocuments?.partTimeWorkCertificate || []);
    }

    next.portfolio = normalizeFileList(payload?.certificationDocuments?.portfolio || []);
    next.portfolioConfirmation = normalizeFileList(payload?.certificationDocuments?.portfolioConfirmationDocuments || []);

    return next;
}

export default function CertificationEditPage() {
    const {id} = useParams();
    const {t} = useTranslation();
    const [payload, setPayload] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [submitErrorVisible, setSubmitErrorVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [notFound, setNotFound] = useState(false);
    const isFirstAutosaveRef = useRef(true);

    useMetaTags({
        title: t('certifications.title'),
        description: t('app.description'),
        metas: [{name: 'keywords', content: t('app.keywords')}],
    });

    const isDraft = Number(payload?.status) === DRAFT_STATUS;
    const isPortfolioRequest = Number(payload?.requestType) === PORTFOLIO_REQUEST_TYPE;

    const dictionaries = useMemo(() => ({
        types: Array.isArray(payload?.dictionaries?.types) ? payload.dictionaries.types : [],
        categories: Array.isArray(payload?.dictionaries?.categories) ? payload.dictionaries.categories : [],
        specialities: Array.isArray(payload?.dictionaries?.specialities) ? payload.dictionaries.specialities : [],
    }), [payload]);

    const previewReady = useMemo(() => isPreviewReady(form), [form]);
    const certificationReady = useMemo(() => isCertificationReady(form), [form]);
    const portfolioReady = useMemo(() => isPortfolioReady(form), [form]);

    const fetchPageData = useCallback(() => {
        setIsLoading(true);
        setNotFound(false);

        fetch(`/api/v1/certifications/${id}`, {credentials: 'same-origin'})
            .then(async (response) => {
                const json = await response.json().catch(() => null);
                return {response, json};
            })
            .then(({response, json}) => {
                const {kind, redirectUrl} = parseRouteResponse(response, json);

                if (kind === 'not_found') {
                    setPayload(null);
                    setNotFound(true);
                    return;
                }

                if ((kind === 'unauthorized' || kind === 'redirect') && redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }

                if (kind === 'error') {
                    window.location.href = '/certifications';
                    return;
                }

                if (json?.state !== 'active') {
                    window.location.href = '/certifications';
                    return;
                }

                setPayload(json);
                setForm(normalizeInitialForm(json));
                setCurrentStep(1);
                setUploadError('');
                setSubmitErrorVisible(false);
                isFirstAutosaveRef.current = true;
            })
            .catch(() => {
                window.location.href = '/certifications';
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id]);

    useEffect(() => {
        fetchPageData();
    }, [fetchPageData]);

    const saveDraft = useCallback(() => {
        if (!payload?.actions?.patchUrl || !isDraft) {
            return;
        }

        fetch(payload.actions.patchUrl, {
            method: 'PATCH',
            credentials: 'same-origin',
            body: buildPatchFormData(form),
        }).catch(() => {});
    }, [form, isDraft, payload]);

    useEffect(() => {
        if (!payload || !isDraft) {
            return;
        }

        if (isFirstAutosaveRef.current) {
            isFirstAutosaveRef.current = false;
            return;
        }

        const timer = setTimeout(() => {
            saveDraft();
        }, 400);

        return () => {
            clearTimeout(timer);
        };
    }, [form, isDraft, payload, saveDraft]);

    const updateFieldValue = (fieldName, value) => {
        setForm((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };

    const removeFile = (fieldName, fileIndex) => {
        setForm((prev) => ({
            ...prev,
            [fieldName]: (prev[fieldName] || []).filter((_, index) => index !== fileIndex),
        }));
    };

    const uploadFile = async (fieldName, file) => {
        const tempId = `tmp-${Date.now()}-${Math.random()}`;

        setForm((prev) => ({
            ...prev,
            [fieldName]: [
                ...(prev[fieldName] || []),
                {
                    id: '',
                    name: file.name,
                    tempId,
                    uploading: true,
                },
            ],
        }));

        const body = new FormData();
        body.append('document', file);

        try {
            const response = await fetch('/api/v1/uploads/document', {
                method: 'POST',
                credentials: 'same-origin',
                body,
            });
            const json = await response.json();
            const fileId = String(json?.id || json?.fileIdentifier || '');

            if (!response.ok || fileId === '') {
                throw new Error('upload_failed');
            }

            setForm((prev) => ({
                ...prev,
                [fieldName]: (prev[fieldName] || []).map((item) => {
                    if (item.tempId !== tempId) {
                        return item;
                    }

                    return {
                        id: fileId,
                        name: file.name,
                    };
                }),
            }));
            setUploadError('');
        } catch (error) {
            setForm((prev) => ({
                ...prev,
                [fieldName]: (prev[fieldName] || []).filter((item) => item.tempId !== tempId),
            }));
            setUploadError(t('certification.documents.uploadAllRequired'));
        }
    };

    const handleFileChange = (fieldName, maxFiles, event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';

        if (files.length === 0) {
            return;
        }

        const existing = Array.isArray(form[fieldName]) ? form[fieldName] : [];
        const availableSlots = Math.max(0, maxFiles - existing.length);

        files.slice(0, availableSlots).forEach((file) => {
            uploadFile(fieldName, file);
        });
    };

    const submitCertification = (event) => {
        event.preventDefault();

        if (!payload?.actions?.submitUrl || !isDraft) {
            return;
        }

        const canSubmit = isPortfolioRequest ? portfolioReady : certificationReady;
        setSubmitErrorVisible(!canSubmit);

        if (!canSubmit) {
            return;
        }

        setIsSubmitting(true);

        fetch(payload.actions.submitUrl, {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('submit_failed');
                }

                window.location.href = payload.actions?.listUrl || '/certifications';
            })
            .catch(() => {
                setSubmitErrorVisible(true);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const renderFileUpload = (fieldName, title, maxFiles = 1) => {
        const inputId = `upload-${fieldName}`;
        const files = Array.isArray(form[fieldName]) ? form[fieldName] : [];

        return (
            <div className={`file-upload-${fieldName} file-upload`}>
                <div className="certification-request-upload-file-text">{title}</div>
                <div className="file-upload-block">
                    <div className="flex">
                        <input
                            id={inputId}
                            style={{display: 'none'}}
                            type="file"
                            multiple={maxFiles > 1}
                            onChange={(event) => handleFileChange(fieldName, maxFiles, event)}
                            disabled={!isDraft || isSubmitting || files.length >= maxFiles}
                        />
                        <button
                            type="button"
                            className="btn btn-default upload-button"
                            disabled={!isDraft || isSubmitting || files.length >= maxFiles}
                            onClick={() => {
                                const node = document.getElementById(inputId);
                                if (node) {
                                    node.click();
                                }
                            }}>
                            {t('certification.request.upload')}
                        </button>
                    </div>
                    <div className="file-list">
                        {files.map((file, index) => (
                            <div className="file-item" key={`${fieldName}-${file.id || file.tempId || index}`}>
                                {file.name}{file.uploading ? ' ...' : ''}
                                {isDraft && (
                                    <i
                                        className="icon-delete"
                                        onClick={() => removeFile(fieldName, index)}>
                                    </i>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    if (isLoading) {
        return null;
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (!payload) {
        return null;
    }

    return (
        <section id="certification-create" className="spa-certification-create">
            <form id="certification-form" onSubmit={submitCertification}>
                {!isPortfolioRequest && (
                    <div className="certification-steps">
                        {[1, 2, 3, 4].map((step) => (
                            <React.Fragment key={`step-${step}`}>
                                {step > 1 && <div className="certification-step-arrow"></div>}
                                <div
                                    className={`certification-step ${currentStep === step ? 'active' : ''}`}
                                    data-step={step}
                                    onClick={() => setCurrentStep(step)}>
                                    <div className={`certification-step-number ${currentStep === step ? 'active' : ''}`}>
                                        <span className="certification-number-text-block">{step}</span>
                                    </div>
                                    <div className="certification-step-description">
                                        {step === 1 && t('certification.steps.attestationData')}
                                        {step === 2 && t('certification.steps.request')}
                                        {step === 3 && t('certification.steps.attestationList')}
                                        {step === 4 && t('certification.steps.uploadDocuments')}
                                    </div>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                )}

                {!isPortfolioRequest && currentStep === 1 && (
                    <div className="certification-form">
                        <div className="certification-form-group">
                            <label className="form-label selectLabel required" htmlFor="type">{t('certification.attestationData.type')}</label>
                            <select
                                className="form-select"
                                id="type"
                                name="type"
                                required
                                value={form.type}
                                disabled={!isDraft || isSubmitting}
                                onChange={(event) => updateFieldValue('type', event.target.value)}>
                                <option value=""></option>
                                {dictionaries.types.map((typeItem) => (
                                    <option key={`type-${typeItem.id}`} value={typeItem.id}>{t(typeItem.name)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="certification-form-group">
                            <label className="form-label selectLabel required" htmlFor="category">{t('certification.attestationData.category')}</label>
                            <select
                                className="form-select"
                                id="category"
                                name="qualificationCategory"
                                required
                                value={form.qualificationCategory}
                                disabled={!isDraft || isSubmitting}
                                onChange={(event) => updateFieldValue('qualificationCategory', event.target.value)}>
                                <option value=""></option>
                                {dictionaries.categories.map((categoryItem) => (
                                    <option key={`category-${categoryItem.id}`} value={categoryItem.id}>{t(categoryItem.name)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="certification-form-group">
                            <label className="form-label selectLabel required" htmlFor="speciality">{t('certification.attestationData.specialty')}</label>
                            <select
                                className="form-select"
                                id="speciality"
                                name="speciality"
                                required
                                value={form.speciality}
                                disabled={!isDraft || isSubmitting}
                                onChange={(event) => updateFieldValue('speciality', event.target.value)}>
                                <option value=""></option>
                                {dictionaries.specialities.map((specialityItem) => (
                                    <option key={`speciality-${specialityItem.id}`} value={specialityItem.id}>{specialityItem.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="certification-button-block">
                            <button type="button" className="btn" onClick={() => setCurrentStep(2)}>{t('certification.nextStep')}</button>
                        </div>
                    </div>
                )}

                {!isPortfolioRequest && currentStep === 2 && (
                    <div className="certification-form">
                        <div className="certification-confirmation-request-text">{t('certification.request.confirmationCaption')}</div>
                        <div className="certification-request-form-download-block">
                            <div className="text-block">{t('certification.request.getExample')}</div>
                            <button
                                type="button"
                                className="btn"
                                onClick={() => {
                                    window.location.href = payload.actions?.requestExampleUrl || '/documents/requestExample.docx';
                                }}>
                                {t('certification.request.open')}
                            </button>
                        </div>
                        {renderFileUpload('certificationRequest', t('certification.request.requestCaption'), 1)}
                        <div className="certification-button-block">
                            <button type="button" className="btn" onClick={() => setCurrentStep(3)}>{t('certification.nextStep')}</button>
                        </div>
                    </div>
                )}

                {!isPortfolioRequest && currentStep === 3 && (
                    <div className="certification-form personal-data">
                        <div className="certification-form-group">
                            <div className="certification-paper-form-label">{t('certification.personalData.header')}</div>
                            <div className="username">
                                <label className="form-label required" htmlFor="username">{t('certification.personalData.username')}</label>
                                <input
                                    type="text"
                                    name="username"
                                    id="username"
                                    maxLength="150"
                                    className="form-input"
                                    required
                                    value={form.username}
                                    disabled={!isDraft || isSubmitting}
                                    onChange={(event) => updateFieldValue('username', event.target.value)}
                                />
                            </div>
                            <div className="date-block birthday-date">
                                <div className="input-wrap">
                                    <label className="form-label calendar-label" htmlFor="birthday">{t('certification.personalData.birthdayDate')}</label>
                                    <i className="calendar-form-icon"></i>
                                    <input
                                        type="text"
                                        id="birthday"
                                        name="birthdayDate"
                                        className="form-input"
                                        value={form.birthdayDate}
                                        disabled={!isDraft || isSubmitting}
                                        onChange={(event) => updateFieldValue('birthdayDate', event.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="certification-form-group">
                            <div className="certification-paper-form-label">{t('certification.personalData.education')}</div>
                            <div className="username" style={{marginRight: 0}}>
                                <label className="form-label required" htmlFor="universityName">{t('certification.personalData.universityName')}</label>
                                <input
                                    type="text"
                                    name="universityName"
                                    id="universityName"
                                    maxLength="150"
                                    className="form-input"
                                    required
                                    value={form.universityName}
                                    disabled={!isDraft || isSubmitting}
                                    onChange={(event) => updateFieldValue('universityName', event.target.value)}
                                />
                            </div>
                            <div className="username">
                                <label className="form-label required" htmlFor="yearEnd">{t('certification.personalData.universityYearEnd')}</label>
                                <input
                                    type="text"
                                    name="yearEnd"
                                    id="yearEnd"
                                    maxLength="150"
                                    className="form-input"
                                    required
                                    value={form.yearEnd}
                                    disabled={!isDraft || isSubmitting}
                                    onChange={(event) => updateFieldValue('yearEnd', event.target.value)}
                                />
                            </div>
                            <div className="username">
                                <label className="form-label required" htmlFor="faculty">{t('certification.personalData.faculty')}</label>
                                <input
                                    type="text"
                                    name="faculty"
                                    id="faculty"
                                    maxLength="150"
                                    className="form-input"
                                    required
                                    value={form.faculty}
                                    disabled={!isDraft || isSubmitting}
                                    onChange={(event) => updateFieldValue('faculty', event.target.value)}
                                />
                            </div>
                        </div>

                        <div className="certification-form-group">
                            <div className="certification-paper-form-label">{t('certification.personalData.workplace')}</div>
                            <div className="username" style={{marginRight: 0}}>
                                <label className="form-label required" htmlFor="workplaceName">{t('certification.personalData.workplaceName')}</label>
                                <input
                                    type="text"
                                    name="workplaceName"
                                    id="workplaceName"
                                    maxLength="150"
                                    className="form-input"
                                    required
                                    value={form.workplaceName}
                                    disabled={!isDraft || isSubmitting}
                                    onChange={(event) => updateFieldValue('workplaceName', event.target.value)}
                                />
                            </div>
                            <div className="username">
                                <label className="form-label required" htmlFor="score">{t('certification.personalData.score')}</label>
                                <input
                                    type="text"
                                    name="score"
                                    id="score"
                                    maxLength="150"
                                    className="form-input"
                                    required
                                    value={form.score}
                                    disabled={!isDraft || isSubmitting}
                                    onChange={(event) => updateFieldValue('score', event.target.value)}
                                />
                            </div>
                            <div className="username" style={{marginRight: 0}}>
                                <label className="form-label required" htmlFor="workplacePositionName">{t('certification.personalData.position')}</label>
                                <input
                                    type="text"
                                    name="workplacePositionName"
                                    id="workplacePositionName"
                                    maxLength="150"
                                    className="form-input"
                                    required
                                    value={form.workplacePositionName}
                                    disabled={!isDraft || isSubmitting}
                                    onChange={(event) => updateFieldValue('workplacePositionName', event.target.value)}
                                />
                            </div>
                            <div className="username">
                                <label className="form-label required" htmlFor="experience">{t('certification.personalData.experience')}</label>
                                <input
                                    type="text"
                                    name="experience"
                                    id="experience"
                                    maxLength="150"
                                    className="form-input"
                                    required
                                    value={form.experience}
                                    disabled={!isDraft || isSubmitting}
                                    onChange={(event) => updateFieldValue('experience', event.target.value)}
                                />
                            </div>
                        </div>

                        <div className="certification-form-group" style={{display: 'flex'}}>
                            <button
                                type="button"
                                className="btn-preview-attestation"
                                disabled={!previewReady}
                                onClick={() => {
                                    if (payload.actions?.attestationListUrl) {
                                        window.location.href = payload.actions.attestationListUrl;
                                    }
                                }}>
                                {t('certification.personalData.view')}
                            </button>
                        </div>

                        <div className="certification-button-block">
                            <button type="button" className="btn" onClick={() => setCurrentStep(4)}>{t('certification.nextStep')}</button>
                        </div>
                    </div>
                )}

                {!isPortfolioRequest && currentStep === 4 && (
                    <div className="certification-form">
                        <div className="certification-confirmation-request-text">{t('certification.documents.confirmationCaption')}</div>
                        {renderFileUpload('diploma', t('certification.documents.diplomaCopy'), 2)}
                        {renderFileUpload('employmentHistory', t('certification.documents.workbookCopy'), 10)}
                        {renderFileUpload('specialistCertificate', t('certification.documents.specialistCertificate'), 1)}
                        {renderFileUpload('qualificationCategoryCertificate', t('certification.documents.categoryCertificate'), 1)}
                        {renderFileUpload('partTimeWork', t('certification.documents.partTimeWork'), 1)}
                        {renderFileUpload('portfolio', t('certification.documents.portfolio'), 20)}
                        {renderFileUpload('portfolioConfirmation', t('certification.documents.otherDocuments'), 50)}
                        <div className="certification-button-block">
                            {(submitErrorVisible || uploadError) && (
                                <div className="js-error" style={{color: '#E21839', textAlign: 'center'}}>
                                    {uploadError || t('certification.documents.uploadAllRequired')}
                                </div>
                            )}
                            <button type="submit" className="btn" disabled={!isDraft || isSubmitting || !certificationReady}>
                                {t('certification.documents.submit')}
                            </button>
                        </div>
                    </div>
                )}

                {isPortfolioRequest && (
                    <div className="certification-form">
                        <h1>{t('certification.portfolio.header')}</h1>
                        {renderFileUpload('portfolio', t('certification.portfolio.portfolioCaption'), 20)}
                        {renderFileUpload('portfolioConfirmation', t('certification.portfolio.otherDocuments'), 50)}
                        <div className="certification-button-block">
                            {(submitErrorVisible || uploadError) && (
                                <div className="js-error" style={{color: '#E21839', textAlign: 'center'}}>
                                    {uploadError || t('certification.portfolio.uploadAllRequired')}
                                </div>
                            )}
                            <button type="submit" className="btn" disabled={!isDraft || isSubmitting || !portfolioReady}>
                                {t('certification.portfolio.submit')}
                            </button>
                        </div>
                    </div>
                )}

            </form>
        </section>
    );
}
